import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2, FileText, CheckCircle, AlertCircle, Shield } from 'lucide-react';
import { SignaturePad } from '../components/SignaturePad';
import { useTranslation } from '../i18n';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function GuestSignPage() {
  const { t } = useTranslation();
  const { token } = useParams();
  const [signatureOpen, setSignatureOpen] = useState(false);
  const [signed, setSigned] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(null);

  // Fetch contract details
  const { data, isLoading, error } = useQuery({
    queryKey: ['guest-sign', token],
    queryFn: async () => {
      const response = await fetch(`${API_URL}/api/guest/sign/${token}`);
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Invalid or expired signing link');
      }
      return response.json();
    },
  });

  // Sign mutation
  const signMutation = useMutation({
    mutationFn: async ({ signerName, signatureData }) => {
      const response = await fetch(`${API_URL}/api/guest/sign/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          signer_name: signerName,
          signature_data: signatureData,
        }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to sign contract');
      }
      return response.json();
    },
    onSuccess: () => {
      setSigned(true);
    },
  });

  const handleSign = (signResult) => {
    signMutation.mutate({
      signerName: signResult.signer_name || data?.signer?.name || "Guest",
      signatureData: signResult.signature_data,
    });
    setSignatureOpen(false);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400 mx-auto mb-4" />
          <p className="text-muted-foreground">{t("guestSign.loading")}</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">{t("guestSign.unableToLoad")}</h2>
            <p className="text-muted-foreground">{error.message}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Signed confirmation
  if (signed) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-semibold mb-2">{t("guestSign.signed")}</h2>
            <p className="text-muted-foreground mb-6">
              Your signature has been recorded. Both parties will receive a copy of the signed contract.
            </p>
            <div className="border-t pt-6 mt-6">
              <p className="text-sm text-muted-foreground/60 mb-3">{t("guestSign.createOwn")}</p>
              <Link to="/signup">
                <Button variant="outline" className="w-full">
                  Create a Free PocketFiler Account
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Get contract content
  const contract = data?.contract;
  const signer = data?.signer;
  const sender = data?.sender;

  // Handle multi-language content
  const contentLanguages = contract?.content ? Object.keys(contract.content) : [];
  const displayLanguage = selectedLanguage || contentLanguages[0] || 'en';
  const contractText = contract?.content?.[displayLanguage] ||
    (typeof contract?.content === 'string' ? contract.content : 'No contract content available');

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-card border-b">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-gray-600" />
            <span className="font-semibold text-foreground">PocketFiler</span>
          </div>
          <div className="flex items-center gap-1 text-xs text-muted-foreground/60">
            <Shield className="h-3 w-3" />
            <span>{t("guestSign.secureSign")}</span>
          </div>
        </div>
      </div>

      {/* Contract content */}
      <div className="max-w-3xl mx-auto px-4 py-6 md:py-8">
        {/* Contract header */}
        <div className="mb-6">
          <h1 className="text-xl md:text-2xl font-semibold text-gray-900 mb-1">{contract?.title || 'Contract'}</h1>
          {sender?.organization_name && (
            <p className="text-muted-foreground break-words">From: {sender.organization_name}</p>
          )}
          {signer?.name && (
            <p className="text-muted-foreground break-words">To: {signer.name}</p>
          )}
        </div>

        {/* Language selector (if multi-language) */}
        {contentLanguages.length > 1 && (
          <div className="flex gap-2 mb-4">
            {contentLanguages.map(lang => (
              <Button
                key={lang}
                variant={displayLanguage === lang ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedLanguage(lang)}
              >
                {lang.toUpperCase()}
              </Button>
            ))}
          </div>
        )}

        {/* Contract text */}
        <Card className="mb-8">
          <CardContent className="pt-6">
            <div className="prose prose-gray break-words max-w-none whitespace-pre-wrap text-sm leading-relaxed">
              {contractText}
            </div>
          </CardContent>
        </Card>

        {/* Sign button */}
        <div className="sticky bottom-0 bg-background py-4 border-t">
          <p className="text-xs text-muted-foreground text-center mb-3 px-4">
            By signing this document, you agree that your electronic signature is legally binding under the ESIGN Act,
            you have reviewed the document and intend to sign, and PocketFiler may record your IP address and browser for verification.
          </p>
          <Button
            onClick={() => setSignatureOpen(true)}
            className="w-full h-12 text-base"
            disabled={signMutation.isPending}
          >
            {signMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Signing...
              </>
            ) : (
              'Sign This Contract'
            )}
          </Button>
          {signMutation.error && (
            <Alert variant="destructive" className="mt-3">
              <AlertDescription>{signMutation.error.message}</AlertDescription>
            </Alert>
          )}
        </div>
      </div>

      {/* Signature pad modal */}
      <SignaturePad
        open={signatureOpen}
        onOpenChange={setSignatureOpen}
        onSign={handleSign}
        isLoading={signMutation.isPending}
        signerName={signer?.name || ''}
        contractTitle={contract?.title || 'Contract'}
      />
    </div>
  );
}
